import type { DebriefPresentation, MissionResult } from "../core/engine";
import { ArmoryScreen } from "../hud/armory";
import { MainMenuScreen } from "../hud/main-menu";
import { MissionSelectScreen } from "../hud/mission-select";
import { ModDraftScreen } from "../hud/mod-draft";
import { PreMissionScreen } from "../hud/pre-mission";
import { RunMapScreen } from "../hud/run-map";
import { RunOverScreen } from "../hud/run-over";
import {
  applyEscalation,
  effectiveEscalation,
  sortieDarkness,
  totalClears,
  unlockedNodeKinds,
} from "../meta/campaign";
import { computeRunModifiers, draftChoices, fieldModById } from "../meta/field-mods";
import { canPullMail, pullMail } from "../meta/mail";
import { pickPreflight } from "../meta/preflight";
import { buildSigintPuzzle, applySigintReward } from "../mission/sigint";
import { DEFAULT_LOADOUT, type LoadoutMods } from "../meta/loadout";
import { randomSeed } from "../meta/rng";
import {
  addDraftedMod,
  applyResupply,
  completeMission,
  createRun,
} from "../meta/run";
import type { RunNode, RunState } from "../meta/run-types";
import { bankRun, healOperatorsForNewRun, loadSave, writeSave, type SaveData } from "../meta/save";
import { computeRosterModifiers, rankFor } from "../meta/operators";
import { armoryUnlockById, draftPoolIds, hasPerk, sensorModesFor } from "../meta/unlocks";
import { BarracksScreen } from "../hud/barracks";
import { buildNodeMission, scalingForNode, type TheaterKey } from "../mission/variants";
import { TheaterSelectScreen } from "../hud/theater-select";
import type { MissionConfig } from "../mission/types";

// Orchestrates the whole out-of-mission game: main menu, operations (runs),
// drafts, the Armory, and the legacy test range. This is the ONLY place meta
// state (the save) mutates, and it never mutates during an active mission —
// results land on debrief.

export type TestRangeCard = {
  config: MissionConfig;
  desc: string;
  hint: string;
};

export type MissionLaunchHooks = {
  onMissionResult: (result: MissionResult) => DebriefPresentation;
  onReturnToMenu: () => void;
};

export type MissionLauncher = (
  config: MissionConfig,
  loadout: LoadoutMods,
  hooks: MissionLaunchHooks,
) => void;

export class RunFlow {
  private save: SaveData;
  private readonly menu: MainMenuScreen;
  private readonly runMap: RunMapScreen;
  private readonly draft: ModDraftScreen;
  private readonly armory: ArmoryScreen;
  private readonly runOver: RunOverScreen;
  private readonly preMission: PreMissionScreen;
  private readonly testRange: MissionSelectScreen;
  private readonly barracks: BarracksScreen;
  private readonly theaterSelect: TheaterSelectScreen;
  // Where the debrief's CONTINUE should land, decided when the result comes in
  private afterDebrief: (() => void) | null = null;

  constructor(
    private readonly launchMission: MissionLauncher,
    testRangeCards: TestRangeCard[],
  ) {
    this.save = loadSave();

    this.menu = new MainMenuScreen({
      onContinueRun: () => this.openRunMap(),
      onNewRun: () => this.openTheaterSelect(),
      onArmory: () => this.openArmory(),
      onBarracks: () => this.openBarracks(),
      onTestRange: () => this.openTestRange(),
      onMailPull: () => this.pullMailBag(),
    });
    this.runMap = new RunMapScreen({
      onEnterNode: (node) => this.enterNode(node),
      onAbandon: () => this.abandonRun(),
    });
    this.draft = new ModDraftScreen();
    this.armory = new ArmoryScreen({
      onBuy: (id) => this.buyUnlock(id),
      onBack: () => this.showMenu(),
    });
    this.runOver = new RunOverScreen();
    this.preMission = new PreMissionScreen();
    this.barracks = new BarracksScreen({ onBack: () => this.showMenu() });
    this.theaterSelect = new TheaterSelectScreen({
      onPick: (theater) => this.newRun(theater),
      onBack: () => this.showMenu(),
    });
    this.testRange = new MissionSelectScreen(testRangeCards, (config) => {
      this.testRange.hide();
      this.startTestRangeMission(config);
    }, () => this.showMenu());
  }

  showMenu(): void {
    this.hideAll();
    this.menu.setState({
      hasActiveRun: this.save.activeRun !== null && this.save.activeRun.outcome === "active",
      opsPoints: this.save.opsPoints,
      bestDepth: this.save.career.bestDepth,
      campaignDay: this.save.campaign.day,
      log: this.save.log,
      mail: this.save.mail,
      canPullMail: canPullMail(this.save.mail, this.save.campaign.day),
    });
    this.menu.show();
  }

  // The Mail Bag ritual: one pull per campaign day, pure flavor. The pull
  // itself is random — a ritual, not a puzzle.
  private pullMailBag(): void {
    if (!canPullMail(this.save.mail, this.save.campaign.day)) return;
    const living = this.save.operators.map((op) => op.name);
    this.save = {
      ...this.save,
      mail: pullMail(this.save.mail, this.save.campaign.day, randomSeed(), living),
    };
    writeSave(this.save);
    this.showMenu();
  }

  private hideAll(): void {
    this.menu.hide();
    this.runMap.hide();
    this.draft.hide();
    this.armory.hide();
    this.runOver.hide();
    this.preMission.hide();
    this.testRange.hide();
    this.barracks.hide();
    this.theaterSelect.hide();
  }

  private openTheaterSelect(): void {
    this.hideAll();
    this.theaterSelect.setCampaign(this.save.campaign);
    this.theaterSelect.show();
  }

  private openBarracks(): void {
    this.hideAll();
    this.barracks.setProfile(this.save);
    this.barracks.show();
  }

  // --- Operations ----------------------------------------------------------

  private newRun(theater: TheaterKey): void {
    // Starting over abandons a live operation (its ops still bank — earned
    // is earned).
    if (this.save.activeRun && this.save.activeRun.outcome === "active") {
      this.save = bankRun(this.save, { ...this.save.activeRun, outcome: "defeat" });
    }
    // Deploy the readiest operators (highest XP first); wounded sit this one
    // out and heal for the run after. Selection happens BEFORE the heal tick.
    const ready = this.save.operators
      .filter((op) => op.status === "ready")
      .sort((a, b) => b.xp - a.xp)
      .map((op) => op.name);
    this.save = healOperatorsForNewRun(this.save);
    // createRun deploys the front of the list up to squadMax and benches the
    // rest as reserves. The campaign decides how hot the theater is and
    // which taskings the run map may roll.
    const run = createRun(randomSeed(), this.save.armoryUnlocks, {
      roster: ready,
      reserves: ready,
    }, theater, {
      escalation: effectiveEscalation(this.save.campaign, theater),
      allowedKinds: unlockedNodeKinds(totalClears(this.save.campaign)),
    });
    this.save = { ...this.save, activeRun: run };
    writeSave(this.save);

    if (hasPerk(this.save.armoryUnlocks, "opening-draft")) {
      this.openDraft();
      return;
    }
    this.openRunMap();
  }

  private get run(): RunState | null {
    return this.save.activeRun;
  }

  private updateRun(run: RunState): void {
    this.save = { ...this.save, activeRun: run };
    writeSave(this.save);
  }

  private openRunMap(banner = ""): void {
    const run = this.run;
    if (!run || run.outcome !== "active") {
      this.showMenu();
      return;
    }
    this.hideAll();
    this.runMap.setRun(run, banner);
    this.runMap.show();
  }

  private abandonRun(): void {
    const run = this.run;
    if (run) {
      this.save = bankRun(this.save, { ...run, outcome: "defeat" });
      writeSave(this.save);
    }
    this.showMenu();
  }

  private enterNode(node: RunNode): void {
    const run = this.run;
    if (!run) return;

    if (node.kind === "resupply") {
      const after = applyResupply(run, node.id);
      this.updateRun(after);
      const newName = after.roster.find((n) => !run.roster.includes(n));
      this.openRunMap(newName
        ? `RESUPPLY DROP — ${newName} joins Alpha. Squad at ${after.squadSize}/${after.squadMax}.`
        : `RESUPPLY DROP — Alpha already at full strength (${after.squadSize}/${after.squadMax}).`);
      return;
    }

    const mods = computeRunModifiers(run.modIds);
    const scaling = applyEscalation(
      scalingForNode(node, mods.reinforcementBudgetDelta, run.squadSize),
      run.escalation,
    );
    const config = buildNodeMission(node, scaling, run.theater);
    const squadless = config.squadSize === 0;
    if (!squadless) {
      // Operator traits stack with drafted field mods
      const deployed = this.save.operators.filter((op) => run.roster.includes(op.name));
      const traits = computeRosterModifiers(deployed);
      config.squadDamageTakenMult = mods.squadDamageTakenMult * traits.squadDamageTakenMult;
      config.squadNames = run.roster;
      config.enemyHitChanceMult = traits.enemyHitChanceMult;
      config.squadFireEffectMult = traits.squadFireEffectMult;
      config.deathPreventions = traits.deathPreventions;
    }
    config.aircraftDamageTakenMult = mods.aircraftDamageTakenMult;
    config.flareCount = 3 + mods.bonusFlares;
    // FIRST FLIGHT hints ride the first few career missions, then get out
    // of the way. Session-deduped inside the ticker.
    config.showHints = this.save.career.missions < 3;
    // The sensor suite is earned (THEME.md): base DAYLIGHT + WHITE-HOT,
    // BLACK-HOT and EO-GREEN are Armory packages.
    config.sensorModes = sensorModesFor(this.save.armoryUnlocks);
    // Night sorties open on the thermal; on NG+ theaters they upgrade to
    // BLACKOUT — the sensor package is down, EO only, Tab is dead.
    const darkness = sortieDarkness(node.night === true, this.save.campaign.theaters[run.theater].clears);
    if (darkness === "night") {
      config.sensorDefault = "white-hot";
      config.name = `${config.name} — NIGHT`;
    } else if (darkness === "blackout") {
      config.sensorDefault = "eo-green";
      config.sensorLocked = true;
      config.name = `${config.name} — BLACKOUT`;
    }

    // Hub-texture rituals (docs/design/HUB.md): SIGINT on hard missions,
    // the Pre-Flight Check on every run sortie. Rewards apply to the config
    // before launch — never mid-mission.
    const sigintSeed = (run.seed ^ (node.col * 2654435761) ^ (node.row * 40503)) >>> 0;
    const sigint = node.kind === "elite" || node.kind === "final"
      ? (() => {
          const puzzle = buildSigintPuzzle(config, sigintSeed);
          return {
            fragments: puzzle.fragments,
            onPick: (index: number) => {
              if (index !== puzzle.correctIndex) {
                return { correct: false, line: "Nothing actionable in that traffic. No harm done." };
              }
              return { correct: true, line: applySigintReward(config) };
            },
          };
        })()
      : undefined;
    const preflight = {
      questions: pickPreflight(sigintSeed ^ 0x5f3759df),
      onPassed: () => {
        config.flareCount = (config.flareCount ?? 3) + 1;
      },
    };

    this.hideAll();
    this.preMission.show({
      config,
      ...(sigint ? { sigint } : {}),
      preflight,
      squadLine: squadless
        ? "NO FRIENDLIES ON THE GROUND — WEAPONS FREE"
        : `ALPHA: ${run.roster
            .map((name) => {
              const op = this.save.operators.find((o) => o.name === name);
              const rank = op ? rankFor(op.xp) : "RECRUIT";
              return rank === "RECRUIT" ? name : `${name} <span class="op-rank op-rank--${rank.toLowerCase()}">${rank[0]}</span>`;
            })
            .join(" · ")} &nbsp;(${run.squadSize}/${run.squadMax})`,
      modLines: run.modIds.map((id) => this.modName(id)),
      onStart: () => this.launchRunMission(node, config, mods.loadout),
      onBack: () => this.openRunMap(),
    });
  }

  private modName(id: string): string {
    return fieldModById(id)?.name ?? id;
  }

  private launchRunMission(node: RunNode, config: MissionConfig, loadout: LoadoutMods): void {
    this.launchMission(config, loadout, {
      onMissionResult: (result) => this.onRunMissionResult(node, result),
      onReturnToMenu: () => {
        const next = this.afterDebrief ?? (() => this.showMenu());
        this.afterDebrief = null;
        next();
      },
    });
  }

  private onRunMissionResult(node: RunNode, result: MissionResult): DebriefPresentation {
    const run = this.run;
    if (!run) return { rewardLines: [], continueLabel: "RETURN TO MENU" };

    // Squad-less fire missions never touch the roster — Alpha sat this one
    // out at the FOB.
    const squadless = node.kind === "destroy" || node.kind === "ambush" || node.kind === "sead";
    const outcome = completeMission(run, node.id, {
      success: result.success,
      kills: result.stats.kills,
      squadAlive: squadless ? run.squadSize : result.stats.squadAlive,
      survivors: squadless ? run.roster : result.stats.survivors,
      ffIncidents: result.stats.ffIncidents,
    });

    const ffLine = result.stats.ffIncidents > 0
      ? [`FRIENDLY FIRE: ${result.stats.ffIncidents} INCIDENT${result.stats.ffIncidents > 1 ? "S" : ""} — OPS DOCKED`]
      : [];

    if (outcome.next === "draft") {
      this.updateRun(outcome.run);
      this.afterDebrief = () => this.openDraft();
      return {
        rewardLines: [`+${outcome.opsAwarded} OPS`, ...ffLine, "RESUPPLY DROP INBOUND — FIELD MOD DRAFT"],
        continueLabel: "CONTINUE OPERATION",
      };
    }

    // Run ended — bank it now (crash-safe), show the summary after debrief.
    const finished = outcome.run;
    this.save = bankRun({ ...this.save, activeRun: null }, finished);
    writeSave(this.save);
    this.afterDebrief = () => this.runOver.show(finished, () => this.showMenu());
    return {
      rewardLines: [`+${outcome.opsAwarded} OPS`, ...ffLine],
      continueLabel: outcome.next === "run-victory" ? "OPERATION COMPLETE" : "OPERATION OVER",
    };
  }

  private openDraft(): void {
    const run = this.run;
    if (!run || run.outcome !== "active") {
      this.showMenu();
      return;
    }
    const pool = draftPoolIds(this.save.armoryUnlocks);
    const choices = draftChoices(pool, run.modIds, run.seed, run.clearedNodeIds.length);
    if (choices.length === 0) {
      this.openRunMap();
      return;
    }
    this.hideAll();
    this.draft.show(choices, (modId) => {
      if (modId === null) {
        this.updateRun({ ...run, opsEarned: run.opsEarned + 1 });
        this.openRunMap("Draft declined — +1 ops banked.");
        return;
      }
      const after = addDraftedMod(run, modId);
      this.updateRun(after);
      this.openRunMap(`${this.modName(modId)} fitted.`);
    });
  }

  // --- Armory ---------------------------------------------------------------

  private openArmory(): void {
    this.hideAll();
    this.armory.setProfile(this.save);
    this.armory.show();
  }

  private buyUnlock(unlockId: string): void {
    const unlock = armoryUnlockById(unlockId);
    if (!unlock) return;
    if (this.save.armoryUnlocks.includes(unlockId) || this.save.opsPoints < unlock.cost) return;
    this.save = {
      ...this.save,
      opsPoints: this.save.opsPoints - unlock.cost,
      armoryUnlocks: [...this.save.armoryUnlocks, unlockId],
    };
    writeSave(this.save);
    this.armory.setProfile(this.save);
  }

  // --- Test range -------------------------------------------------------------

  private openTestRange(): void {
    this.hideAll();
    this.testRange.show();
  }

  private startTestRangeMission(config: MissionConfig): void {
    this.preMission.show({
      config,
      squadLine: "TEST RANGE — STANDARD FIRETEAM, NO REQUISITIONS",
      modLines: [],
      onStart: () => {
        this.launchMission(config, DEFAULT_LOADOUT, {
          onMissionResult: () => ({
            rewardLines: ["TEST RANGE — NOTHING BANKED"],
            continueLabel: "RETURN TO MENU",
          }),
          onReturnToMenu: () => this.showMenu(),
        });
      },
      onBack: () => this.openTestRange(),
    });
  }
}
